/* ==============================================================================
   Tela de login
   ==============================================================================
   Segue a referência: card claro, campos com linha inferior e ícone, botão em
   pílula com gradiente, opções externas em botões circulares, rodapé com a ação
   alternativa em caixa alta.

   Regra que não se negocia, independente do visual: nenhuma resposta desta tela
   revela se um e-mail existe. A mensagem é sempre a mesma, e o servidor equaliza
   até a latência dos dois casos (ver auth/service.ts, fakeVerify).
   ============================================================================== */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  AuthShell,
  CenteredLabel,
  ErrorSlot,
  Field,
  FieldGroup,
  FooterPrompt,
  InlineLink,
  LockIcon,
  MailIcon,
  PasswordToggle,
  SocialRow,
  SubmitButton,
} from './AuthShell.tsx';

export type LoginStep = 'credentials' | 'totp';

export interface OidcProvider {
  id: string;
  label: string;
  /** Cor sólida do círculo. Cor de marca do provider. */
  color: string;
  icon: React.ReactNode;
}

export interface LoginScreenProps {
  onSubmitCredentials: (email: string, password: string) => Promise<void>;
  onSubmitTotp: (code: string) => Promise<void>;
  onOidcLogin: (providerId: string) => void;
  oidcProviders?: ReadonlyArray<OidcProvider>;
  allowRegistration?: boolean;
  onRegisterClick?: () => void;
  onForgotPasswordClick?: () => void;
  step: LoginStep;
  /** Mensagem já pronta para o usuário. Nunca detalhe técnico. */
  error?: string | null;
  loading?: boolean;
  /** Bloqueia o envio SEM parecer "carregando" (ex.: back-end fora do ar). */
  disabled?: boolean;
  instanceName?: string;
}

export function LoginScreen({
  onSubmitCredentials,
  onSubmitTotp,
  onOidcLogin,
  oidcProviders = [],
  allowRegistration = false,
  onRegisterClick,
  onForgotPasswordClick,
  step,
  error,
  loading = false,
  disabled = false,
  instanceName = 'Concord',
}: LoginScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);
  const totpRef = useRef<HTMLInputElement>(null);

  // Foco no campo certo de cada etapa. Sem isso o usuário precisa clicar depois
  // do 2FA aparecer, o que quebra o fluxo de digitar-tab-digitar.
  useEffect(() => {
    if (step === 'credentials') emailRef.current?.focus();
    else totpRef.current?.focus();
  }, [step]);

  const blocked = loading || disabled;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (blocked) return;
    if (step === 'credentials') void onSubmitCredentials(email, password);
    else void onSubmitTotp(totp);
  };

  return (
    <AuthShell
      title={step === 'credentials' ? 'Entrar' : 'Verificação'}
      instanceName={instanceName}
      footer={
        step === 'credentials' && allowRegistration && onRegisterClick ? (
          <FooterPrompt
            text="Ainda não tem conta?"
            actionLabel="Criar conta"
            onAction={onRegisterClick}
          />
        ) : undefined
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {step === 'credentials' ? (
          <FieldGroup>
            <Field
              ref={emailRef}
              id="email"
              label="E-mail"
              type="email"
              value={email}
              onChange={setEmail}
              autoComplete="username"
              placeholder="Digite seu e-mail"
              disabled={blocked}
              required
              icon={<MailIcon />}
            />

            <div>
              <Field
                id="password"
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                placeholder="Digite sua senha"
                disabled={blocked}
                required
                icon={<LockIcon />}
                trailing={
                  <PasswordToggle
                    shown={showPassword}
                    onToggle={() => setShowPassword((v) => !v)}
                  />
                }
              />

              {onForgotPasswordClick && (
                <InlineLink
                  label="Esqueceu a senha?"
                  onClick={onForgotPasswordClick}
                />
              )}
            </div>
          </FieldGroup>
        ) : (
          <Field
            ref={totpRef}
            id="totp"
            label="Código de verificação"
            type="text"
            value={totp}
            /* Só dígitos, no máximo 6: o campo se recusa a aceitar entrada
               inválida em vez de aceitar e reclamar depois. */
            onChange={(v) => setTotp(v.replace(/\D/g, '').slice(0, 6))}
            autoComplete="one-time-code"
            inputMode="numeric"
            placeholder="000000"
            disabled={blocked}
            required
            mono
          />
        )}

        <ErrorSlot message={error} />

        <SubmitButton loading={loading} disabled={disabled}>
          {loading ? 'Entrando' : step === 'credentials' ? 'Entrar' : 'Verificar'}
        </SubmitButton>
      </form>

      {/* Só aparece quando há provider REALMENTE configurado. Ícone que leva a
          404 porque falta credencial é pior que ícone ausente. */}
      {step === 'credentials' && oidcProviders.length > 0 && (
        <>
          <CenteredLabel>Ou entre usando</CenteredLabel>
          <SocialRow
            providers={oidcProviders}
            onSelect={onOidcLogin}
            disabled={blocked}
          />
        </>
      )}
    </AuthShell>
  );
}
